import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Coins, Swords } from "lucide-react";
import { useLeagueContext } from "@/modules/leagues/hooks/useLeagueContext";
import { useMatch } from "@/modules/leagues/hooks/useMatches";
import { useLeagueParticipants } from "@/modules/leagues/hooks/useLeagueParticipants";
import { useCreateDuel } from "@/modules/leagues/hooks/useDuels";
import { ScoreStepper } from "@/modules/leagues/components/ScoreStepper";
import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import { Label } from "@/components/atoms/Label";
import { Skeleton } from "@/components/atoms/Skeleton";
import { Avatar, AvatarFallback } from "@/components/atoms/Avatar";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { ROUTES } from "@/constants/routes";
import { cn } from "@/utils/cn";

/**
 * Criação de Duelo 1x1 (pedido do usuário): escolhe adversário da liga,
 * dá seu palpite pra essa partida específica e define o valor em R$
 * combinado — o app só REGISTRA o combinado, nunca processa pagamento.
 */
export function CreateDuelPage() {
  const { league } = useLeagueContext();
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { firebaseUser } = useAuth();

  const { data: match, isLoading: loadingMatch } = useMatch(league.id, matchId);
  const { data: participants, isLoading: loadingParticipants } = useLeagueParticipants(league.id);
  const createDuel = useCreateDuel(league.id);

  const [opponentUid, setOpponentUid] = useState<string | null>(null);
  const [homeScore, setHomeScore] = useState(0);
  const [awayScore, setAwayScore] = useState(0);
  const [valor, setValor] = useState(20);

  const opponents = (participants ?? []).filter((p) => p.uid !== firebaseUser?.uid);
  const canSubmit = Boolean(opponentUid) && valor > 0 && match?.status === "scheduled" && !match?.predictionsLocked;

  function handleSubmit() {
    if (!match || !opponentUid) return;
    const opponent = opponents.find((p) => p.uid === opponentUid);
    if (!opponent) return;

    createDuel.mutate(
      {
        matchId: match.id,
        round: match.round,
        matchKickoff: match.kickoff,
        desafiadoUid: opponent.uid,
        desafiadoNome: opponent.nome,
        desafiantePalpite: { homeScore, awayScore },
        valorReais: valor,
      },
      {
        onSuccess: () => {
          toast({ title: "Desafio enviado!", description: `${opponent.nome} vai receber seu desafio.` });
          navigate(ROUTES.duels(league.id));
        },
        onError: (err) =>
          toast({
            title: "Não foi possível enviar o desafio",
            description: err instanceof Error ? err.message : "Tente novamente.",
            variant: "destructive",
          }),
      }
    );
  }

  return (
    <div className="space-y-4 pb-4">
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate(ROUTES.leaguePredictions(league.id))}
          aria-label="Voltar"
          className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <h1 className="font-display text-base font-bold">Novo Duelo 1x1</h1>
      </div>

      {(loadingMatch || loadingParticipants) && (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      )}

      {match && !match.predictionsLocked && match.status === "scheduled" && (
        <>
          <div className="rounded-2xl border border-border bg-card p-4">
            <div className="flex items-center justify-center gap-4">
              <div className="flex flex-col items-center gap-1">
                {match.homeTeam.crestUrl && <img src={match.homeTeam.crestUrl} alt="" className="h-9 w-9" />}
                <span className="text-xs font-medium">{match.homeTeam.name}</span>
              </div>
              <span className="text-muted-foreground">×</span>
              <div className="flex flex-col items-center gap-1">
                {match.awayTeam.crestUrl && <img src={match.awayTeam.crestUrl} alt="" className="h-9 w-9" />}
                <span className="text-xs font-medium">{match.awayTeam.name}</span>
              </div>
            </div>
          </div>

          <div>
            <Label className="mb-2 block">1. Escolha o adversário</Label>
            <div className="grid grid-cols-2 gap-2">
              {opponents.map((p) => (
                <button
                  key={p.uid}
                  onClick={() => setOpponentUid(p.uid)}
                  className={cn(
                    "flex items-center gap-2 rounded-xl border p-2.5 text-left transition-colors",
                    opponentUid === p.uid ? "border-primary bg-primary-50 dark:bg-primary-900/30" : "border-border bg-card hover:bg-muted"
                  )}
                >
                  <Avatar size="sm">
                    <AvatarFallback>
                      {p.nome.split(" ").slice(0, 2).map((n) => n[0]).join("")}
                    </AvatarFallback>
                  </Avatar>
                  <span className="truncate text-sm font-medium">{p.nome}</span>
                </button>
              ))}
            </div>
            {opponents.length === 0 && (
              <p className="text-xs text-muted-foreground">Não há outros participantes nesta liga ainda.</p>
            )}
          </div>

          <div>
            <Label className="mb-2 block">2. Seu palpite pra essa partida</Label>
            <div className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-center justify-around">
                <ScoreStepper label={match.homeTeam.shortName} value={homeScore} onChange={setHomeScore} />
                <ScoreStepper label={match.awayTeam.shortName} value={awayScore} onChange={setAwayScore} />
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="valor-duelo" className="mb-2 block">
              3. Valor combinado (R$)
            </Label>
            <div className="relative">
              <Coins className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="valor-duelo"
                type="number"
                min={1}
                step={1}
                value={valor}
                onChange={(e) => setValor(Number(e.target.value))}
                className="pl-9"
              />
            </div>
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              O app só registra o valor combinado — o acerto é feito entre vocês, fora do app.
            </p>
          </div>

          <Button className="w-full" onClick={handleSubmit} disabled={!canSubmit || createDuel.isPending}>
            <Swords className="h-4 w-4" />
            {createDuel.isPending ? "Enviando..." : "Enviar desafio"}
          </Button>
        </>
      )}

      {match && (match.predictionsLocked || match.status !== "scheduled") && (
        <p className="rounded-xl border border-border bg-card px-4 py-6 text-center text-sm text-muted-foreground">
          Não é mais possível desafiar alguém para esta partida — os palpites já estão encerrados.
        </p>
      )}
    </div>
  );
}
